/**
 * useArTablePagination
 *
 * Cursor-based Firestore pagination for ArTable.
 * Reads/writes uiState.pagination (Valtio). Cursor cache lives on
 * uiState.paginationCursors (ref-wrapped, non-reactive).
 *
 * Runs at ArTableShell level (above StoreProvider) to produce QueryConstraint[].
 * The row count is NOT here: it needs the rows store, which only exists below
 * StoreProvider, so `useArTableRowCount` owns it from `ArTableBody` (SYD-2451).
 *
 * Invariant: a cursor is only ever handed out under the ordering that produced
 * it. The cache carries that ordering (`paginationCursors.orderByKey`) and is
 * reconciled while rendering — an effect would run after `persistCollection`
 * has already built `startAfter(staleDoc)`, which Firestore rejects
 * synchronously with `invalid-argument` (SYD-2304).
 */

import { useMemo } from 'react';
import { useSnapshot } from 'valtio';
import type { OrderByOption, QueryFilter } from '@/store/Store.types';
import { queryOrderByKey } from '@/store/core/queryOrderByKey';
import type { ArTableUIState } from '../state/createArTableState';

type UseArTablePaginationParams = {
  uiState: ArTableUIState;
  rowsOrderBy: OrderByOption;
};

type UseArTablePaginationReturn = {
  paginationConstraints: QueryFilter[];
};

function useArTablePagination({
  uiState,
  rowsOrderBy,
}: UseArTablePaginationParams): UseArTablePaginationReturn {
  const paginationSnap = useSnapshot(uiState.pagination);
  const { currentPage: snapshotPage, pageSize } = paginationSnap;

  const orderByKey = queryOrderByKey(rowsOrderBy);
  const cursors = uiState.paginationCursors;
  const orderingChanged = cursors.orderByKey !== orderByKey;
  if (orderingChanged) {
    cursors.byPage.clear();
    cursors.orderByKey = orderByKey;
    uiState.pagination.currentPage = 1;
  }
  const currentPage = orderingChanged ? 1 : snapshotPage;

  /** Build query filters from current page + cursor cache */
  const paginationConstraints = useMemo((): QueryFilter[] => {
    const constraints: QueryFilter[] = [{ type: 'limit', n: pageSize }];

    if (currentPage > 1) {
      const cursorDoc = cursors.byPage.get(currentPage - 1);
      if (cursorDoc) {
        constraints.push({ type: 'startAfter', cursor: cursorDoc });
      }
    }

    return constraints;
  }, [currentPage, pageSize, cursors]);

  return { paginationConstraints };
}

export { useArTablePagination };
export type { UseArTablePaginationParams, UseArTablePaginationReturn };
